/* eslint-disable react/destructuring-assignment */
import React, { ReactNode, useCallback, useRef } from 'react';

import { useCustomEventListener } from '@/hooks/useCustomEventListener/useCustomEventListener';

import { TListIcons } from '@/helpers/Tangerina/type';

interface IBaseProps {
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: () => void;
}

interface IButtonIconProps extends IBaseProps {
  appearance: 'iconOnly';
  icon: TListIcons;
  onBrand?: boolean;
  showNotification?: boolean;
  size?: 'sm' | 'lg';
  ariaLabel?: string;
  notificationAriaLabel?: string;
}

interface IButtonIconContainerProps extends IBaseProps {
  appearance: 'iconContainer';
  icon: TListIcons;
  size: 'sm' | 'md' | 'lg';
  ariaLabel?: string;
}

interface IButtonMiniProps extends IBaseProps {
  appearance: 'mini';
  icon?: TListIcons;
  inverse?: boolean;
  children: ReactNode;
}

interface IButtonPrimaryProps extends IBaseProps {
  appearance: 'primary';
  icon?: TListIcons;
  loading?: boolean;
  onBrand?: boolean;
  size?: 'sm' | 'lg';
  ariaLabel?: string;
  children: ReactNode;
}

interface IButtonSecondaryProps extends IBaseProps {
  appearance: 'secondary';
  loading?: boolean;
  onBrand?: boolean;
  size?: 'sm' | 'lg';
  ariaLabel?: string;
  children: ReactNode;
}

type TButtonProps =
  | IButtonIconProps
  | IButtonIconContainerProps
  | IButtonMiniProps
  | IButtonPrimaryProps
  | IButtonSecondaryProps;

export default function Button(props: TButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { onClick } = props;

  const handleClick = useCallback(() => {
    onClick?.();
  }, [onClick]);

  useCustomEventListener(buttonRef, 'tgrclick', handleClick);

  if (props.appearance === 'iconOnly') {
    return (
      <tgr-button-icon
        ref={buttonRef}
        type={props.type}
        icon={props.icon as any}
        disabled={props.disabled || undefined}
        on-brand={props.onBrand || undefined}
        show-notification={props.showNotification}
        size={props.size}
        data-aria-label={props.ariaLabel}
        notification-data-aria-label={props.notificationAriaLabel}
      />
    );
  }

  if (props.appearance === 'iconContainer') {
    return (
      <tgr-button-icon-container
        ref={buttonRef}
        disabled={props.disabled || undefined}
        icon={props.icon as any}
        size={props.size}
        type={props.type}
        data-aria-label={props.ariaLabel}
      />
    );
  }

  if (props.appearance === 'mini') {
    return (
      <tgr-button-mini
        ref={buttonRef}
        disabled={props.disabled || undefined}
        icon={props.icon as any}
        inverse={props.inverse || undefined}
        type={props.type}
      >
        {props.children}
      </tgr-button-mini>
    );
  }

  if (props.appearance === 'primary') {
    return (
      <tgr-button-primary
        ref={buttonRef}
        disabled={props.disabled || undefined}
        icon={props.icon}
        loading={props.loading || undefined}
        on-brand={props.onBrand || undefined}
        size={props.size}
        type={props.type}
        data-aria-label={props.ariaLabel}
      >
        {props.children}
      </tgr-button-primary>
    );
  }

  if (props.appearance === 'secondary') {
    return (
      <tgr-button-secondary
        ref={buttonRef}
        disabled={props.disabled || undefined}
        loading={props.loading || undefined}
        on-brand={props.onBrand || undefined}
        size={props.size}
        type={props.type}
        data-aria-label={props.ariaLabel}
      >
        {props.children}
      </tgr-button-secondary>
    );
  }

  return null;
}
